import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getApiUrl } from '../utils/tokenUtils';
import LandingPageView from './LandingPageView';

export function LandingDocumentView() {
  const { url, tab } = useParams();
  const [attempt, setAttempt] = useState(0);
  const [document, setDocument] = useState({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const timeout = setTimeout(() => controller.abort(), 15000);
    setDocument({ status: 'loading' });

    const loadDocument = async () => {
      try {
        const response = await fetch(getApiUrl(`/api/landing-pages/public/${encodeURIComponent(url)}/documents/${encodeURIComponent(tab)}`), {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) {
          throw new Error('Document unavailable');
        }
        const { html } = await response.json();
        if (typeof html !== 'string') throw new Error('Document unavailable');
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        parsed.querySelectorAll('script, style').forEach(element => element.remove());
        if (!parsed.body.textContent.trim() || parsed.getElementById('root')) {
          throw new Error('Empty document');
        }
        if (active) setDocument({ status: 'ready', html, title: parsed.title || 'Legal Document' });
      } catch (error) {
        if (active) setDocument({ status: 'error' });
      } finally {
        clearTimeout(timeout);
      }
    };

    loadDocument();
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [url, tab, attempt]);

  if (document.status === 'ready') {
    return (
      <>
        <Helmet><title>{document.title}</title></Helmet>
        <iframe
          title={document.title}
          srcDoc={document.html}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          className="block h-screen w-full border-0 bg-white"
        />
      </>
    );
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-4 bg-white px-6 text-center text-gray-900">
      {document.status === 'loading' ? <p role="status">Loading document...</p> : (
        <>
          <h1 className="text-2xl font-semibold">Document unavailable</h1>
          <p role="alert">We couldn’t load this document. Please try again.</p>
          <button className="rounded-lg bg-gray-900 px-5 py-2 text-white" onClick={() => setAttempt(value => value + 1)}>Try again</button>
          <Link className="underline" to={`/landing/${encodeURIComponent(url)}`}>Back to app</Link>
        </>
      )}
    </main>
  );
}

export default function LandingPageRoute() {
  const { tab } = useParams();
  return tab?.toLowerCase() === 'support' ? <LandingPageView /> : <LandingDocumentView />;
}
